import {
  MIN_PLAYERS, PHASES, STEP_KINDS, pageCount, kindOfPage, bookHeldBy, everyoneDone,
} from './rounds';

// State transitions. Pure functions, plain JSON in and out - same shape as the
// no-thanks / space-base / thunderstone engines, so the store can be thin and
// the whole thing is testable without a browser.
//
// Unlike those, this game is SIMULTANEOUS: there is no active player and no
// turn to pass. A step ends when everybody has put something on the page they
// are holding. That difference is why there's no `dispatch(state, action)`
// reducer here - every write is "this seat submitted this page", and the only
// other transition is "the step is over".

export const createGame = ({ players, startedAt = null }) => {
  if (!Array.isArray(players) || players.length < MIN_PLAYERS) {
    throw new Error(`Telestrations needs at least ${MIN_PLAYERS} players, got ${players?.length ?? 0}`);
  }
  const pages = pageCount(players.length);
  return {
    phase: PHASES.PLAYING,
    step: 0,
    pages,
    startedAt,
    players: players.map((player, index) => ({
      id: player.id,
      name: player.name || `Player ${index + 1}`,
      seat: index,
    })),
    // One book per player, indexed by the owner's seat. `pages` is a sparse
    // array indexed by step - page 0 is the starting word, and so on.
    books: players.map((player, index) => ({
      owner: index,
      ownerName: player.name || `Player ${index + 1}`,
      pages: [],
    })),
    // Reveal bookkeeping, unused until the game finishes.
    reveal: { book: 0, page: 0 },
  };
};

const clone = (state) => ({
  ...state,
  players: state.players.map((p) => ({ ...p })),
  books: state.books.map((b) => ({ ...b, pages: [...b.pages] })),
  reveal: { ...state.reveal },
});

/**
 * Records one page. `content` is a string for a word or guess, or a stroke
 * array for a drawing.
 *
 * Refuses to overwrite a page that already exists: a double-submit (a slow
 * network, a double tap, two tabs) must not clobber what somebody drew.
 */
export const submitPage = (state, seat, content) => {
  if (state.phase !== PHASES.PLAYING) return state;
  const book = bookHeldBy(seat, state.step, state.players.length);
  if (state.books[book]?.pages?.[state.step]) return state;

  const next = clone(state);
  const kind = kindOfPage(state.step);
  next.books[book].pages[state.step] = {
    kind,
    by: state.players[seat]?.id ?? null,
    byName: state.players[seat]?.name ?? null,
    // Words are trimmed; drawings are kept exactly as given.
    content: kind === STEP_KINDS.DRAW ? content : String(content ?? '').trim(),
    at: state.startedAt === null ? null : Date.now(),
  };
  return next;
};

/**
 * Moves to the next step, or into the reveal if the books are full.
 *
 * `presence` lets the caller decide the step is over while somebody is
 * offline - see fillMissingPages, which is what makes that safe.
 */
export const advanceStep = (state, presence = null) => {
  if (state.phase !== PHASES.PLAYING) return state;
  if (!everyoneDone(state, presence)) return state;

  const next = clone(state);
  next.step += 1;
  if (next.step >= next.pages) {
    next.phase = PHASES.REVEAL;
    next.step = next.pages;
    next.reveal = { book: 0, page: 0 };
  }
  return next;
};

/**
 * Fills in blanks for anyone who didn't submit, so the chain isn't broken by
 * a dropped connection.
 *
 * A missing page is recorded honestly rather than skipped: the next player
 * gets something to react to, and the reveal shows a gap where somebody went
 * offline instead of silently pretending the book is complete.
 */
export const fillMissingPages = (state, seats) => {
  let next = state;
  for (const seat of seats) {
    const kind = kindOfPage(next.step);
    next = submitPage(next, seat, kind === STEP_KINDS.DRAW ? [] : '');
  }
  return next;
};

export const isBlankPage = (page) => {
  if (!page) return true;
  if (page.kind === STEP_KINDS.DRAW) return !page.content || page.content.length === 0;
  return !String(page.content || '').trim();
};

// ---- reveal ----------------------------------------------------------------

/**
 * Steps the shared reveal forward: through the pages of the current book,
 * then on to the next book, then done.
 *
 * Everyone watches the same page at the same time (Matt's call) - so this
 * lives in the shared state rather than in each client's head.
 */
export const advanceReveal = (state) => {
  if (state.phase !== PHASES.REVEAL) return state;
  const next = clone(state);
  const bookPages = next.books[next.reveal.book]?.pages?.length ?? 0;
  if (next.reveal.page + 1 < bookPages) {
    next.reveal.page += 1;
    return next;
  }
  if (next.reveal.book + 1 < next.books.length) {
    next.reveal.book += 1;
    next.reveal.page = 0;
    return next;
  }
  next.reveal.done = true;
  return next;
};

export const revealBackwards = (state) => {
  if (state.phase !== PHASES.REVEAL) return state;
  const next = clone(state);
  if (next.reveal.done) { next.reveal.done = false; return next; }
  if (next.reveal.page > 0) { next.reveal.page -= 1; return next; }
  if (next.reveal.book > 0) {
    next.reveal.book -= 1;
    next.reveal.page = Math.max(0, (next.books[next.reveal.book]?.pages?.length ?? 1) - 1);
  }
  return next;
};

/**
 * Whose book is being shown, and therefore who drives the reveal. The owner
 * turns their own pages; everyone else follows.
 */
export const revealDriver = (state) => (
  state.phase === PHASES.REVEAL ? state.books[state.reveal.book]?.owner ?? null : null
);

export { PHASES, STEP_KINDS, MIN_PLAYERS };
